import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  MAX_DESCRIPTION_LENGTH,
  MESSAGE_TYPE,
} from "../../../../../../common/constants/common.constants";
import { CancellationType } from "../../../../../../common/enums/proposals.enums";
import { IProposalCancellationReason } from "../../../../../../common/interfaces/proposals.interface";
import { proposalsStore } from "../../../../../../contexts/proposalContext";
import { createNotification } from "../../../../../../utilities/notification";
import FormButtons from "../../../../../FormButtons/FormButtons";
import leftArrow from "../../../../../../assets/backIcon.svg";
import rightArrow from "../../../../../../assets/arrowRightWhite.svg";
import Modal from "../../../../../Modal/Modal";
import CancellationReason from "./CancellationReason";
import "./CancellationModal.scss";
import { toggleModalOpenClass } from "../../../../../../utilities/helpers";
import { getCancellationReasons } from "../../../../../../api/proposals.api";

const CancellationModal: FC<{
  closeModal: () => void;
  cancellationType: CancellationType;
  submitFunction: (
    e: any,
    reasonId: number,
    customReasonMessage?: string
  ) => Promise<void>;
}> = ({ closeModal, cancellationType, submitFunction }) => {
  const { cancellationReasons, setCancellationReasons } = proposalsStore();

  const [activeCancellationReason, setActiveCancellationReason] =
    useState<{ reason: IProposalCancellationReason; index: number }>();
  const [customReasonDescription, setCustomReasonDescription] = useState("");
  const [errorMessage, setErrorMessage] = useState<string>();
  const [submitting, toggleSubmitting] = useState(false);

  useEffect(() => {
    const existingCancellations = cancellationReasons.get(cancellationType);
    if (!existingCancellations || existingCancellations.length === 0) {
      void loadCancellationReasons();
    } else {
      setCancellationReasons(cancellationType, existingCancellations);
    }
  }, []);

  const handleSubmit = useCallback(
    async (e?) => {
      if (!activeCancellationReason?.reason.id) {
        createNotification(
          MESSAGE_TYPE.ERROR,
          "You did not select cancellation reason!"
        );
        return;
      }
      toggleSubmitting(true);

      await submitFunction(
        e,
        activeCancellationReason!.reason.id,
        customReasonDescription
      );
      toggleSubmitting(false);
      closeModal();
      toggleModalOpenClass(false);
    },
    [submitFunction, activeCancellationReason, customReasonDescription]
  );

  const loadCancellationReasons = async () => {
    try {
      setCancellationReasons(
        cancellationType,
        await getCancellationReasons(cancellationType)
      );
    } catch (error) {
      createNotification(
        MESSAGE_TYPE.ERROR,
        "Failed to load cancellation reasons"
      );
    }
  };

  const updateCustomCancellationReason = (e) => {
    if (e.target.value.length > MAX_DESCRIPTION_LENGTH) {
      setErrorMessage("Description can not exceed 200 characters");
      return;
    }
    setErrorMessage(undefined);
    setCustomReasonDescription(e.target.value);
  };

  const changeCancellationReason = useCallback(
    (cancellationReasonId: number, index: number) => {
      const foundCancellationReason = cancellationReasons
        .get(cancellationType)
        ?.find((reason) => reason.id === cancellationReasonId);
      if (foundCancellationReason) {
        setActiveCancellationReason({
          reason: { ...foundCancellationReason },
          index: index,
        });
      } else {
        createNotification(
          MESSAGE_TYPE.ERROR,
          "Failed to select cancellation reason"
        );
      }
    },
    [cancellationReasons]
  );

  return (
    <Modal closeModal={closeModal} width="520px">
      <div className="cancellation-modal">
        <div className="cancellation-modal__header">
          <h2>Why do you want to cancel?</h2>
          <p>(Choose a reason)</p>
        </div>
        <div className="cancellation-modal__reasons">
          {cancellationReasons.get(cancellationType)?.map((reason, index) => {
            return (
              <CancellationReason
                key={reason.id}
                isActive={activeCancellationReason?.reason.id === reason.id}
                cancelationReaason={reason}
                onCancellationChosen={() =>
                  changeCancellationReason(reason.id, index)
                }
              />
            );
          })}
        </div>
        <input
          type="text"
          placeholder="Description"
          className="cancellation-modal__other-description"
          onChange={updateCustomCancellationReason}
          value={customReasonDescription}
          disabled={
            cancellationReasons.get(cancellationType) &&
            activeCancellationReason?.index !==
              cancellationReasons.get(cancellationType)!.length - 1
          }
        />
        {errorMessage && (
          <p className="cancellation-modal__error-message">{errorMessage}</p>
        )}
        <hr />
        <FormButtons
          cancelLabel="Go back"
          disabled={!!errorMessage}
          onCancel={() => {
            closeModal();
            toggleModalOpenClass(false);
          }}
          submitLabel="Cancel proposal"
          submitting={submitting}
          typeCancel="button"
          typeSubmit="submit"
          cancelIcon={leftArrow}
          submitIcon={rightArrow}
          onSubmit={handleSubmit}
        />
      </div>
    </Modal>
  );
};

export default CancellationModal;
